import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import type { PermissionCode } from "@/features/auth/permissions";
import {
    getAdminDetails,
    getPermissionsCatalog,
    updateAdmin,
    updateAdminPermissions,
} from "../api/adminsApi";
import type { Admin, UpdateAdminRequest } from "../types";
import { PermissionCatalogEditor } from "../ui/PermissionCatalogEditor";

type RouteState = {
    from?: string;
};

const buildReturnPath = (adminId: string | undefined, routeState: RouteState | null) => {
    if (routeState?.from && routeState.from.trim()) {
        return routeState.from;
    }

    return adminId ? `/admin-management/${adminId}` : "/admin-management";
};

export const EditAdminRoute = () => {
    const { adminId } = useParams<{ adminId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const routeState = (location.state as RouteState | null) ?? null;
    const returnPath = useMemo(
        () => buildReturnPath(adminId, routeState),
        [adminId, routeState]
    );

    const [formData, setFormData] = useState<UpdateAdminRequest>({
        firstName: "",
        lastName: "",
        phoneNumber: "",
    });
    const [selectedPermissions, setSelectedPermissions] = useState<PermissionCode[]>([]);
    const [errors, setErrors] = useState<
        Partial<Record<keyof UpdateAdminRequest | "permissions", string>>
    >({});

    const adminQuery = useQuery({
        queryKey: ["adminDetails", adminId],
        queryFn: () => getAdminDetails(adminId!),
        enabled: !!adminId,
    });

    const permissionCatalogQuery = useQuery({
        queryKey: ["permissions-catalog"],
        queryFn: getPermissionsCatalog,
    });

    const admin = adminQuery.data?.data as Admin | undefined;
    const permissionCatalog = permissionCatalogQuery.data?.data.permissions ?? {};

    useEffect(() => {
        if (!admin) return;

        setFormData({
            firstName: admin.firstName,
            lastName: admin.lastName,
            phoneNumber: admin.phoneNumber ?? "",
        });
        setSelectedPermissions(admin.permissions ?? []);
        setErrors({});
    }, [admin]);

    const updateProfileMutation = useMutation({
        mutationFn: ({ currentAdminId, data }: { currentAdminId: string; data: UpdateAdminRequest }) =>
            updateAdmin(currentAdminId, data),
    });

    const updatePermissionsMutation = useMutation({
        mutationFn: ({
            currentAdminId,
            permissions,
        }: {
            currentAdminId: string;
            permissions: PermissionCode[];
        }) => updateAdminPermissions(currentAdminId, { permissions }),
    });

    const validateForm = () => {
        const nextErrors: Partial<Record<keyof UpdateAdminRequest | "permissions", string>> = {};

        if (formData.firstName !== undefined && !formData.firstName.trim()) {
            nextErrors.firstName = "First name cannot be empty";
        }

        if (formData.lastName !== undefined && !formData.lastName.trim()) {
            nextErrors.lastName = "Last name cannot be empty";
        }

        if (admin?.role !== "SUPER_ADMIN" && selectedPermissions.length === 0) {
            nextErrors.permissions = "Select at least one permission.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleChange = (field: keyof UpdateAdminRequest, value: string) => {
        setFormData((previous) => ({ ...previous, [field]: value }));
        if (errors[field]) {
            setErrors((previous) => ({ ...previous, [field]: undefined }));
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!validateForm() || !admin) return;

        const changedFields: UpdateAdminRequest = {};

        if (formData.firstName !== admin.firstName) {
            changedFields.firstName = formData.firstName?.trim();
        }

        if (formData.lastName !== admin.lastName) {
            changedFields.lastName = formData.lastName?.trim();
        }

        if ((formData.phoneNumber ?? "") !== (admin.phoneNumber ?? "")) {
            changedFields.phoneNumber = formData.phoneNumber?.trim()
                ? formData.phoneNumber.trim()
                : null;
        }

        const permissionsChanged =
            admin.role !== "SUPER_ADMIN" &&
            JSON.stringify([...selectedPermissions].sort()) !==
                JSON.stringify([...(admin.permissions ?? [])].sort());

        if (!permissionsChanged && Object.keys(changedFields).length === 0) {
            navigate(returnPath);
            return;
        }

        if (!admin.adminId) return;

        try {
            if (Object.keys(changedFields).length > 0) {
                await updateProfileMutation.mutateAsync({
                    currentAdminId: admin.adminId,
                    data: changedFields,
                });
            }

            if (permissionsChanged) {
                await updatePermissionsMutation.mutateAsync({
                    currentAdminId: admin.adminId,
                    permissions: selectedPermissions,
                });
            }

            queryClient.invalidateQueries({ queryKey: ["admins"] });
            queryClient.invalidateQueries({ queryKey: ["adminDetails", admin.adminId] });

            navigate(returnPath, {
                state: { flashMessage: "Admin profile updated successfully." },
            });
        } catch {
            // Error state is rendered below from the mutations.
        }
    };

    const mutationError =
        (updatePermissionsMutation.error as { response?: { data?: { message?: string } } })
            ?.response?.data?.message ||
        (updateProfileMutation.error as { response?: { data?: { message?: string } } })
            ?.response?.data?.message ||
        null;

    const isSaving = updateProfileMutation.isPending || updatePermissionsMutation.isPending;

    if (adminQuery.isLoading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Loading admin profile...
                    </p>
                </div>
            </div>
        );
    }

    if (adminQuery.isError || !admin) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <p className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Failed to load admin profile
                    </p>
                    <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                        Please try again later.
                    </p>
                    <Button onClick={() => navigate("/admin-management")} variant="outline">
                        Back to Admins
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-8">
            <div className="space-y-3">
                <Link
                    to={returnPath}
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                        Edit Admin Profile
                    </h1>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Update profile details and assigned permissions on a dedicated workspace.
                    </p>
                </div>
            </div>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                Admin Details
                            </h2>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                Editing {admin.fullName} ({admin.email})
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs font-medium">
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                {admin.role.replace("_", " ")}
                            </span>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                {admin.status}
                            </span>
                            <span className="rounded-full bg-primary-50 px-3 py-1 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300">
                                {admin.effectivePermissions.length} effective permissions
                            </span>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 p-6">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950/40">
                        <div className="mb-5">
                            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                                Profile Information
                            </h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                Keep the core admin identity details up to date here.
                            </p>
                        </div>

                        <div className="grid gap-4 lg:grid-cols-2">
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(event) =>
                                        handleChange("firstName", event.target.value)
                                    }
                                    className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                        errors.firstName
                                            ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                            : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                    }`}
                                />
                                {errors.firstName ? (
                                    <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                        {errors.firstName}
                                    </p>
                                ) : null}
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(event) =>
                                        handleChange("lastName", event.target.value)
                                    }
                                    className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                        errors.lastName
                                            ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                            : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                    }`}
                                />
                                {errors.lastName ? (
                                    <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                        {errors.lastName}
                                    </p>
                                ) : null}
                            </div>

                            <div className="lg:col-span-2">
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phoneNumber ?? ""}
                                    onChange={(event) =>
                                        handleChange("phoneNumber", event.target.value)
                                    }
                                    placeholder="Optional phone number"
                                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                        </div>
                    </div>

                    {admin.role === "SUPER_ADMIN" ? (
                        <div className="rounded-2xl border border-primary-200 bg-primary-50 px-5 py-4 text-sm text-primary-700 dark:border-primary-900/60 dark:bg-primary-950/20 dark:text-primary-300">
                            Super admin accounts are managed at the role level, so explicit permission assignment is not edited here.
                        </div>
                    ) : permissionCatalogQuery.isLoading ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-10 text-center dark:border-slate-800 dark:bg-slate-900/50">
                            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Loading permission catalog...
                            </p>
                        </div>
                    ) : permissionCatalogQuery.isError ? (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-6 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                            <div className="flex items-start gap-3">
                                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                                <div>
                                    <p className="font-medium">Permission catalog unavailable</p>
                                    <p className="mt-1">
                                        We couldn&apos;t load the permission definitions needed for editing this admin.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <PermissionCatalogEditor
                                catalog={permissionCatalog}
                                selectedPermissions={selectedPermissions}
                                onChange={(permissions) => {
                                    setSelectedPermissions(permissions);
                                    if (errors.permissions) {
                                        setErrors((previous) => ({
                                            ...previous,
                                            permissions: undefined,
                                        }));
                                    }
                                }}
                                disabled={isSaving}
                            />
                            {errors.permissions ? (
                                <p className="text-sm text-rose-600 dark:text-rose-400">
                                    {errors.permissions}
                                </p>
                            ) : null}
                        </div>
                    )}

                    {mutationError ? (
                        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                            {mutationError}
                        </div>
                    ) : null}

                    <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(returnPath)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={
                                isSaving ||
                                (admin.role !== "SUPER_ADMIN" && permissionCatalogQuery.isLoading)
                            }
                        >
                            {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                    </div>
                </form>
            </section>
        </div>
    );
};
